import { useState, useEffect, useRef, useMemo } from 'react';
import { engagementRowKey } from '../utils/engagementRows';
import { applyClientListFilters, defaultMineOnly } from '../utils/clientListFilters';
import { AddActionItemDialog } from './AddActionItemDialog';
import { QueueTab } from './QueueTab';
import { DeliverWorkspace } from './DeliverWorkspace';
import { SizingTab } from './SizingTab';
import { SeedDemoDataButton } from './SeedDemoDataButton';
import { RichDemoDataCard } from './RichDemoDataCard';
import { AIQuickSOW } from './AIQuickSOW';
import { isDemoEnvironment } from './EnvironmentBadge';
import { ConsultantSpaceReview } from './ConsultantSpaceReview';
import { SpaceManagementPanel } from './SpaceManagementPanel';
import { TopNavigation } from './TopNavigation';
import { projectId } from '../utils/supabase/info';
import { getAuthHeaders } from '../utils/apiHeaders';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';
import { 
  Building2, 
  Clock, 
  AlertCircle, 
  CheckCircle2, 
  Filter, 
  Search,
  FileText,
  BarChart3,
  Wand2,
  UserPlus,
  Grid,
  List,
  Plus,
  Loader2,
  X,
  ChevronDown,
  ListChecks,
  FlaskConical,
  Hammer,
  Calculator,
  Trash2
} from 'lucide-react';
import { ClientEngagementCard } from './ClientEngagementCard';
import { SOWList } from './SOWList';
import { AddSOWDialog, type SOWCreatedInfo } from './AddSOWDialog';
import { ScopeReconcileDialog } from './ScopeReconcileDialog';
import { RECONCILE_AFTER_SOW_INTRO, reconcileOfferFor, type ReconcileOffer } from '../utils/reconcileAfterSow';
import { SOWDetailsModal } from './SOWDetailsModal';
// EditSOWDialog removed: executed SOWs are immutable legal contracts.
import { ShareSOWDialog } from './ShareSOWDialog';
import { SOWVersionHistory } from './SOWVersionHistory';
import { CompanySetup } from './CompanySetup';
import { DashboardActionItems } from './DashboardActionItems';
import { ConsultantAnalyticsDashboard } from './ConsultantAnalyticsDashboard';
import { ProposalsList } from './ProposalsList';
import { EngagementCatalog } from './EngagementCatalog';
import { ConfigurationPage } from './ConfigurationPage';
import { FirmWorkspace } from './FirmWorkspace';
import { WizardManagementPage } from './wizard-builder/WizardManagementPage';
import { AICallLog } from './ai/AICallLog';
import { EngagementDebugger } from './EngagementDebugger';
import { useActionItemsUnified } from '../hooks/useActionItemsUnified';
import { useQueue } from '../hooks/useQueue';
import { workSummaryLabels } from '../utils/queueCounts';
import {
  type WorkspaceId,
  WORKSPACES,
  isFirmWorkspace,
  resolveWorkspaceRouter,
  setLastUsedWorkspace,
} from '../utils/workspaceRouter';
import { getTenantFromHostname } from '../utils/tenantDetection';
import { dashboardTabFromPath, dashboardTabFromSlug, dashboardTabPath, firstTabOf, workspaceForTab } from '../utils/dashboardTabs';

interface Engagement {
  companyId: string;
  companyName: string;
  /** One row per engagement (PRD §4.7); null for companies with no engagement yet. */
  engagementId?: string | null;
  engagementName?: string | null;
  engagementCount?: number;
  totalItems: number;
  completedItems: number;
  waitingForConsultant: number;
  waitingForClient: number;
  blockedItems: number;
  progressPercentage: number;
  healthStatus: 'healthy' | 'at-risk' | 'needs-attention';
  lastActivity: string | null;
  spaces?: number;
  onboardingComplete?: boolean;
  spaceDiscoveryComplete?: boolean;
  viewerRole?: string;
  /** The SOW's status, and who the client and engagement belong to (the Active SOWs and My clients filters). */
  status?: string | null;
  companyOwnerId?: string | null;
  companyCreatedBy?: string | null;
  engagementLeadId?: string | null;
  engagementCreatedBy?: string | null;
  assignedConsultants?: unknown[];
}

interface ConsultantDashboardProps {
  accessToken: string;
  currentUser: any;
  onLogout: () => void;
  onViewClientDetails: (companyId: string) => void;
  onViewSOWDetails?: (sowId: string) => void;
  /** SPA navigate callback injected from Router context */
  onNavigate?: (url: string) => void;
  /** The `:tab` segment of `/dashboard/:tab?`, injected from Router context */
  tabSlug?: string;
}

export function ConsultantDashboard({
  accessToken,
  currentUser,
  onLogout,
  onViewClientDetails,
  onViewSOWDetails,
  onNavigate,
  tabSlug,
}: ConsultantDashboardProps) {
  // ---------------------------------------------------------------------------
  // Workspaces: Sell · Deliver · Firm (one shell; the open tab says which one)
  // ---------------------------------------------------------------------------
  const tenant = useMemo(() => getTenantFromHostname(), []);

  const [engagements, setEngagements] = useState<Engagement[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [filterStatuses, setFilterStatuses] = useState<('healthy' | 'at-risk' | 'needs-attention')[]>([]);
  // The old client table's two filters (RESTORE_PLAN R3.4): active SOWs only; my clients (on by default for BD reps)
  const [activeSowsOnly, setActiveSowsOnly] = useState(false);
  // Sell › Catalog's client configuration section loads only when opened
  const [catalogConfigOpen, setCatalogConfigOpen] = useState(false);
  const [myClientsOnly, setMyClientsOnly] = useState(() => defaultMineOnly(currentUser));
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [addSOWDialogOpen, setAddSOWDialogOpen] = useState(false);
  // Spec §4.10: a SOW added to a company that already had an engagement opens the reconcile step when Add SOW closes.
  const [createdSOW, setCreatedSOW] = useState<SOWCreatedInfo | null>(null);
  const [reconcileOffer, setReconcileOffer] = useState<(ReconcileOffer & { companyId: string }) | null>(null);
  const [sowDetailsModalOpen, setSOWDetailsModalOpen] = useState(false);
  // editSOWDialogOpen removed alongside EditSOWDialog.
  const [shareSOWDialogOpen, setShareSOWDialogOpen] = useState(false);
  const [sowVersionHistoryOpen, setSOWVersionHistoryOpen] = useState(false);
  const [selectedSOWId, setSelectedSOWId] = useState<string | null>(null);
  const [autoSeedAttempted, setAutoSeedAttempted] = useState(false);
  const [aiQuickSOWOpen, setAIQuickSOWOpen] = useState(false);
  const [consultingFirm, setConsultingFirm] = useState<any>(null);
  const clientListRef = useRef<HTMLDivElement>(null);
  const [addCompanyDialogOpen, setAddCompanyDialogOpen] = useState(false);
  const [selectedQuestionTemplate, setSelectedQuestionTemplate] = useState<string>('atlassian');
  const [selectedQuestionCategory, setSelectedQuestionCategory] = useState<string>('assets');
  const [addActionItemOpen, setAddActionItemOpen] = useState(false);

  // Unified Action Items Data
  const actionItemsData = useActionItemsUnified({
    accessToken,
    currentUser,
  });

  // The one Queue: every "what needs me" source composed once, with one
  // total, read by the tab badge, the Needs Your Action card and the Queue
  // tab (ledger L22; audit finding C7).
  const queue = useQueue({ accessToken, currentUser, actionItems: actionItemsData });
  const totalWorkItems = queue.total;

  // Check if we're in a demo environment
  const isDemo = isDemoEnvironment();

  // Sync active tab with URL: `/dashboard/<slug>` (utils/dashboardTabs.ts).
  // The slug comes from the router when the dashboard is rendered inside it,
  // otherwise from the address bar.
  // A bare /dashboard opens the workspace the person's role (or their last
  // choice) lands on; a slug names the tab, and the tab names the workspace.
  const getInitialTab = () => {
    const slug = tabSlug !== undefined ? tabSlug : window.location.pathname.split('/').filter(Boolean)[1];
    if (slug) return tabSlug !== undefined ? dashboardTabFromSlug(tabSlug) : dashboardTabFromPath(window.location.pathname);
    const home = resolveWorkspaceRouter(currentUser, [], tenant).defaultWorkspace;
    return isFirmWorkspace(home) ? firstTabOf(home) : 'overview';
  };

  const [activeTab, setActiveTab] = useState<string>(getInitialTab());
  const activeWorkspace = workspaceForTab(activeTab);

  const handleWorkspaceChange = (ws: WorkspaceId) => {
    if (!isFirmWorkspace(ws)) return;
    setLastUsedWorkspace(tenant, ws);
    handleTabChange(firstTabOf(ws));
  };

  // Back/forward (or a link straight to a tab) changes the slug without a
  // click: follow it. A bare /dashboard keeps whatever tab the role landed on.
  useEffect(() => {
    if (tabSlug === undefined || tabSlug === '') return;
    const fromUrl = dashboardTabFromSlug(tabSlug);
    setActiveTab((current) => (current === fromUrl ? current : fromUrl));
  }, [tabSlug]);

  function handleTabChange(value: string) {
    setActiveTab(value);
    const newPath = dashboardTabPath(value);
    if (window.location.pathname === newPath) return;
    if (onNavigate) {
      onNavigate(newPath);
    } else {
      window.history.pushState({}, '', newPath);
    }
  }

  useEffect(() => {
    loadEngagements();
  }, []);

  // Auto-seed demo data
  useEffect(() => {
    const autoSeedIfNeeded = async () => {
      if (isDemo === false) return;
      if (autoSeedAttempted || loading) return;
      if (!loading && engagements.length > 0) {
        const hasConfigItems = engagements.some(e => e.totalItems > 0);
        if (!hasConfigItems) {
          setAutoSeedAttempted(true);
          try {
            const response = await fetch(
              `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consultant/seed-demo-data`,
              {
                method: 'POST',
                headers: {
                  ...getAuthHeaders(accessToken),
                  'Content-Type': 'application/json',
                },
              }
            );
            if (response.ok) {
              const data = await response.json();
              if (data.success) {
                toast.success('Demo data loaded successfully', {
                  description: 'Your dashboard now has sample config items, SOWs, and queue items',
                });
                loadEngagements();
              }
            }
          } catch (error) {
            console.error('[AUTO-SEED] Error:', error);
          }
        }
      }
    };
    autoSeedIfNeeded();
  }, [loading, engagements, autoSeedAttempted, accessToken, isDemo]);

  const loadEngagements = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consultant/engagements`,
        {
          headers: getAuthHeaders(accessToken),
        }
      );

      if (response.ok) {
        const data = await response.json();
        const normalizedEngagements = (data.engagements || []).map((eng: any) => ({
          ...eng,
          totalItems: eng.totalItems ?? 102,
          completedItems: eng.completedItems ?? 0,
          waitingForConsultant: eng.waitingForConsultant ?? 0,
          waitingForClient: eng.waitingForClient ?? 0,
          blockedItems: eng.blockedItems ?? 0,
          progressPercentage: eng.progressPercentage ?? 0,
          spaces: eng.spaces ?? 0,
          onboardingComplete: eng.onboardingComplete ?? false,
          spaceDiscoveryComplete: eng.spaceDiscoveryComplete ?? false,
          lastActivity: eng.lastActivity ?? null,
          healthStatus: eng.healthStatus ?? 'needs-attention',
        }));
        
        setEngagements(normalizedEngagements);
      }
    } catch (error) {
      console.error('Error loading engagements:', error);
      toast.error('Failed to load engagements. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Apply filters
  const filteredEngagements = applyClientListFilters(engagements, { activeOnly: activeSowsOnly, mineOnly: myClientsOnly }, currentUser?.id).filter(engagement => {
    if (searchTerm && !engagement.companyName.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (filterStatuses.length > 0 && !filterStatuses.includes(engagement.healthStatus)) return false;
    return true;
  });

  const isCFAdmin = currentUser.role === 'cf-admin' || currentUser.role === 'cfAdmin';

  // Summary stats: "Needs Your Action" is the Queue's one count.
  const needsActionCount = queue.total;

  const summaryStats = {
    totalClients: engagements.length,
    needingAction: needsActionCount,
    atRisk: engagements.filter(e => e.healthStatus === 'at-risk').length,
    avgProgress: engagements.length > 0
      ? Math.round(engagements.reduce((sum, e) => sum + e.progressPercentage, 0) / engagements.length)
      : 0,
  };

  const workSummary = {
    ...workSummaryLabels({ clients: summaryStats.totalClients, needsYou: summaryStats.needingAction, blocked: summaryStats.atRisk }),
    // While the Queue's sources load, say so instead of showing a number that will change.
    ...(queue.loading ? { needsYou: 'counting what needs you…' } : {}),
  };

  // Workspace label for the header
  const workspaceLabel = WORKSPACES[activeWorkspace]?.label || 'Dashboard';

  // CF_ADMIN_CONTEXT diagnostics
  const hasFirmContext = !!(currentUser.firmId || currentUser.consultingFirmId);
  const activeFirmRole = currentUser.activeRole || currentUser.role;
  
  useEffect(() => {
    console.log('[CF_ADMIN_CONTEXT] === ConsultantDashboard Mount ===');
    console.log('[CF_ADMIN_CONTEXT] role:', activeFirmRole);
    console.log('[CF_ADMIN_CONTEXT] firmId:', currentUser.firmId || currentUser.consultingFirmId || 'UNDEFINED');
    console.log('[CF_ADMIN_CONTEXT] firmName:', currentUser.firmName || 'UNDEFINED');
    console.log('[CF_ADMIN_CONTEXT] isAdmin:', currentUser.isAdmin);
    console.log('[CF_ADMIN_CONTEXT] baseRole:', currentUser.baseRole);
    console.log('[CF_ADMIN_CONTEXT] hasFirmContext:', hasFirmContext);
    if (!hasFirmContext) {
      console.warn('[CF_ADMIN_CONTEXT] ⚠️ No firm context — dashboard modules will be degraded');
    }
  }, [currentUser.firmId, currentUser.consultingFirmId, activeFirmRole]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Missing firm context warning */}
      {!hasFirmContext && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-3 text-center">
          <div className="flex items-center justify-center gap-2 text-amber-800">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm font-medium">
              Missing consulting firm context — some features may not work correctly.
            </span>
            {currentUser.isAdmin && (
              <span className="text-sm">
                Try switching back to System Admin and then to CF Admin again.
              </span>
            )}
          </div>
        </div>
      )}
      <AddActionItemDialog 
        open={addActionItemOpen}
        onClose={() => setAddActionItemOpen(false)}
        companyId={engagements.length === 1 ? engagements[0].companyId : undefined}
        companies={engagements.map(e => ({ companyId: e.companyId, companyName: e.companyName }))}
        accessToken={accessToken}
        currentUser={currentUser}
        onSuccess={() => {
          actionItemsData.refresh();
          loadEngagements();
        }}
      />

      {/* Top Navigation with Workspace Switcher */}
      <TopNavigation
        user={{
          id: currentUser.id,
          name: currentUser.name || currentUser.email?.split('@')[0] || 'Consultant',
          email: currentUser.email,
          role: currentUser.role || 'consultant',
          avatarUrl: currentUser.avatarUrl,
          firmId: currentUser.firmId || currentUser.consultingFirmId,
          firmName: currentUser.firmName,
          isDemo: currentUser.isDemo,
          roles: currentUser.roles,
          activeRole: currentUser.activeRole,
        }}
        companyId={null as any}
        companyName={null as any}
        accessToken={accessToken}
        onLogout={onLogout}
        activeWorkspace={activeWorkspace}
        onWorkspaceChange={handleWorkspaceChange}
        engagements={engagements}
        onNavigate={onNavigate}
      >
        <div className="flex items-center gap-3">
          <Building2 className="size-6 text-blue-600" aria-hidden="true" />
          <div>
            <h1 className="text-xl font-bold">{workspaceLabel}</h1>
            <p className="text-xs text-muted-foreground">{WORKSPACES[activeWorkspace].description}</p>
          </div>
          {activeWorkspace === 'deliver' && (
            <Button size="sm" variant="outline" className="ml-2 gap-2" onClick={() => setAddActionItemOpen(true)}>
              <Plus className="size-4" aria-hidden="true" />
              Add Action Item
            </Button>
          )}
          {activeWorkspace === 'sell' && (
            <Button size="sm" variant="outline" className="ml-2 gap-2" onClick={() => setAIQuickSOWOpen(true)}>
              <Wand2 className="size-4" aria-hidden="true" />
              Generate SOW with AI
            </Button>
          )}
        </div>
      </TopNavigation>

      {/* Main Content — Workspace-routed */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* ====== SELL: from lead to signed SOW ====== */}
        {activeWorkspace === 'sell' && (
          <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
            <TabsList className="flex flex-wrap h-auto p-1 gap-1 justify-start w-full bg-muted/50">
              <TabsTrigger value="proposals" className="whitespace-nowrap">
                <FileText className="size-4 mr-2" aria-hidden="true" />
                Proposals
              </TabsTrigger>
              <TabsTrigger value="sows" className="whitespace-nowrap">
                <FileText className="size-4 mr-2" aria-hidden="true" />
                SOWs
              </TabsTrigger>
              <TabsTrigger value="sizing" className="whitespace-nowrap">
                <Calculator className="size-4 mr-2" aria-hidden="true" />
                Sizing
              </TabsTrigger>
              <TabsTrigger value="catalog" className="whitespace-nowrap">
                <Grid className="size-4 mr-2" aria-hidden="true" />
                Catalog
              </TabsTrigger>
            </TabsList>

              <TabsContent value="proposals">
                <ProposalsList accessToken={accessToken} currentUser={currentUser} />
              </TabsContent>

              {/* Sizing: the scope estimate before the SOW (ledger L05; step 5 of the simplification plan) */}
              <TabsContent value="sizing" className="space-y-6">
                <SizingTab accessToken={accessToken} firmId={currentUser.firmId || currentUser.consultingFirmId || ''} />
              </TabsContent>

              {/* SOWs Tab */}
              <TabsContent value="sows" className="space-y-6">
                <SOWList
                  accessToken={accessToken}
                  onViewSOW={(sowId) => { setSelectedSOWId(sowId); setSOWDetailsModalOpen(true); }}
                  onCreateSOW={() => setAddSOWDialogOpen(true)}
                  currentUser={currentUser}
                  onNavigateToSettings={() => handleTabChange('settings')}
                />
              </TabsContent>


              {/* Catalog: the engagement types the firm sells (read here; edited in Firm › Settings) */}
              <TabsContent value="catalog" className="space-y-6">
                {(currentUser.firmId || currentUser.consultingFirmId) ? (
                  <>
                    <EngagementCatalog accessToken={accessToken} firmId={currentUser.firmId || currentUser.consultingFirmId || ''} readOnly />
                    {/* Each client's configuration overrides, implementation phases and progress tracker (ConfigurationPage, kept; RESTORE_PLAN R3.6) */}
                    <details className="rounded-lg border" data-testid="catalog-company-configuration" onToggle={(e) => setCatalogConfigOpen((e.currentTarget as HTMLDetailsElement).open)}>
                      <summary className="cursor-pointer p-4">Client configuration: overrides, phases and progress</summary>
                      {catalogConfigOpen && (
                        <div className="p-4 pt-0">
                          <ConfigurationPage accessToken={accessToken} currentUser={currentUser} />
                        </div>
                      )}
                    </details>
                  </>
                ) : (
                  <Card>
                    <CardContent className="py-8 text-center">
                      <p className="text-muted-foreground">You need to be associated with a consulting firm to see the catalog.</p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
          </Tabs>
        )}

        {/* ====== DELIVER: from signed SOW to handoff ====== */}
        {activeWorkspace === 'deliver' && (
          <>
            {/* Work summary: one line instead of three cards; the middle number is the Queue's one count (ledger L22) */}
              <p className="mb-6 flex flex-wrap items-center gap-x-2 gap-y-1 text-muted-foreground" data-testid="work-summary">
                <button
                  type="button"
                  className="inline-flex items-center gap-1 hover:underline"
                  onClick={() => {
                    setFilterStatuses([]);
                    setActiveTab('overview');
                    setTimeout(() => { clientListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 100);
                  }}
                >
                  <Building2 className="size-4 text-blue-600" aria-hidden="true" />
                  {workSummary.clients}
                </button>
                <span aria-hidden="true">·</span>
                <button
                  type="button"
                  className={`inline-flex items-center gap-1 hover:underline ${summaryStats.needingAction > 0 ? 'text-orange-700' : ''}`}
                  onClick={() => setActiveTab('review-queue')}
                >
                  <Clock className="size-4 text-orange-600" aria-hidden="true" />
                  {workSummary.needsYou}
                </button>
                <span aria-hidden="true">·</span>
                <button
                  type="button"
                  className={`inline-flex items-center gap-1 hover:underline ${summaryStats.atRisk > 0 ? 'text-red-700' : ''}`}
                  onClick={() => {
                    setFilterStatuses(['at-risk']);
                    setActiveTab('overview');
                    setTimeout(() => { document.querySelector('[data-client-list]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 100);
                  }}
                >
                  <AlertCircle className="size-4 text-red-600" aria-hidden="true" />
                  {workSummary.blocked}
                </button>
              </p>

            {/* Work Tabs */}
            <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
              <div className="w-full">
                <TabsList className="flex flex-wrap h-auto p-1 gap-1 justify-start w-full bg-muted/50">
                  <TabsTrigger value="overview" className="whitespace-nowrap">
                    <Grid className="size-4 mr-2" />
                    Clients
                    <Badge variant="secondary" className="ml-2">{engagements.length}</Badge>
                  </TabsTrigger>
                  <TabsTrigger value="review-queue" className="whitespace-nowrap">
                    <ListChecks className="size-4 mr-2" />
                    Queue
                    {!queue.loading && totalWorkItems > 0 && (
                      <Badge variant="secondary" className="ml-2 bg-orange-50 text-orange-700 border-orange-200">
                        {totalWorkItems}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="deliver" className="whitespace-nowrap">
                    <Hammer className="size-4 mr-2" />
                    Deliver
                  </TabsTrigger>
                  <TabsTrigger value="wizards" className="whitespace-nowrap">
                    <Wand2 className="size-4 mr-2" />
                    Wizards
                  </TabsTrigger>
                  <TabsTrigger value="analytics" className="whitespace-nowrap">
                    <BarChart3 className="size-4 mr-2" />
                    Analytics
                  </TabsTrigger>
                  {isDemo && isCFAdmin && (
                    <TabsTrigger value="demo" className="whitespace-nowrap">
                      <FlaskConical className="size-4 mr-2" />
                      Demo
                    </TabsTrigger>
                  )}
                </TabsList>
              </div>

              {/* Client Overview Tab */}
              <TabsContent value="overview" className="space-y-6">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
                    <Input
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search clients..."
                      className="pl-10"
                    />
                  </div>

                  {activeSowsOnly && (
                    <Badge variant="secondary" className="cursor-pointer hover:bg-destructive hover:text-destructive-foreground transition-colors" onClick={() => setActiveSowsOnly(false)} title="Remove filter">
                      Active SOWs only<X className="size-3 ml-1" />
                    </Badge>
                  )}
                  {myClientsOnly && (
                    <Badge variant="secondary" className="cursor-pointer hover:bg-destructive hover:text-destructive-foreground transition-colors" onClick={() => setMyClientsOnly(false)} title="Remove filter">
                      My clients<X className="size-3 ml-1" />
                    </Badge>
                  )}
                  {filterStatuses.map((status) => (
                    <Badge
                      key={status}
                      variant="secondary"
                      className="cursor-pointer hover:bg-destructive hover:text-destructive-foreground transition-colors"
                      onClick={() => setFilterStatuses(filterStatuses.filter(s => s !== status))}
                      title="Remove filter"
                    >
                      {status === 'healthy' && 'Healthy'}
                      {status === 'at-risk' && 'At Risk'}
                      {status === 'needs-attention' && 'Needs Attention'}
                      <X className="size-3 ml-1" />
                    </Badge>
                  ))}

                  <div className="relative">
                    <Button variant="outline" size="sm" onClick={() => setFilterDropdownOpen(!filterDropdownOpen)}>
                      <Filter className="size-4 mr-2" />Filter<ChevronDown className="size-4 ml-2" />
                    </Button>
                    {filterDropdownOpen && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setFilterDropdownOpen(false)} />
                        <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-md border shadow-lg z-20 p-4">
                          <div className="space-y-4">
                            <h4 className="font-medium text-sm">Filter by Status</h4>
                            <div className="space-y-3">
                              {(['healthy', 'at-risk', 'needs-attention'] as const).map(s => (
                                <label key={s} className="flex items-center gap-2 cursor-pointer">
                                  <Checkbox
                                    checked={filterStatuses.includes(s)}
                                    onCheckedChange={(checked) => {
                                      if (checked) setFilterStatuses([...filterStatuses, s]);
                                      else setFilterStatuses(filterStatuses.filter(st => st !== s));
                                    }}
                                  />
                                  <span className="text-sm capitalize">{s.replace(/-/g, ' ')}</span>
                                </label>
                              ))}
                            </div>
                            <h4 className="font-medium text-sm pt-2 border-t">Show</h4>
                            <div className="space-y-3">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <Checkbox checked={activeSowsOnly} onCheckedChange={(checked) => setActiveSowsOnly(checked === true)} aria-label="Active SOWs only" />
                                <span className="text-sm">Active SOWs only</span>
                              </label>
                              <label className="flex items-center gap-2 cursor-pointer">
                                <Checkbox checked={myClientsOnly} onCheckedChange={(checked) => setMyClientsOnly(checked === true)} aria-label="My clients" />
                                <span className="text-sm">My clients</span>
                              </label>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="flex gap-1 border rounded-lg p-1">
                    <Button variant={viewMode === 'grid' ? 'secondary' : 'ghost'} size="sm" onClick={() => setViewMode('grid')}>
                      <Grid className="size-4" />
                    </Button>
                    <Button variant={viewMode === 'list' ? 'secondary' : 'ghost'} size="sm" onClick={() => setViewMode('list')}>
                      <List className="size-4" />
                    </Button>
                  </div>
                  
                  <Button onClick={() => setAddCompanyDialogOpen(true)} size="sm">
                    <Plus className="size-4 mr-2" />Add Client
                  </Button>
                </div>

                <div ref={clientListRef}>
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="size-8 animate-spin text-gray-400" />
                    </div>
                  ) : filteredEngagements.length === 0 ? (
                    <Card>
                      <CardContent className="py-12 text-center text-muted-foreground">
                        <Building2 className="size-12 mx-auto mb-3 text-gray-400" />
                        <p className="font-medium">No clients found</p>
                        {searchTerm && <p className="text-sm">Try adjusting your search or filters</p>}
                        {!searchTerm && engagements.length === 0 && (
                          <div className="mt-6 space-y-4">
                            <p className="text-sm">Get started by adding your first client engagement</p>
                            <div className="flex justify-center gap-3">
                              <Button onClick={() => setAddCompanyDialogOpen(true)} variant="default">
                                <Plus className="size-4 mr-2" />Add Client
                              </Button>
                              {isDemo && (
                                <SeedDemoDataButton 
                                  accessToken={accessToken}
                                  currentUser={currentUser}
                                  onComplete={() => { toast.success('Demo data loaded successfully'); loadEngagements(); }}
                                />
                              )}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ) : (
                    <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4' : 'space-y-4'}>
                      {filteredEngagements.map(engagement => (
                        <ClientEngagementCard
                          key={engagementRowKey(engagement)}
                          engagement={engagement}
                          onViewDetails={onViewClientDetails}
                          accessToken={accessToken}
                          currentUser={currentUser}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Review Queue Tab */}
              <TabsContent value="review-queue">
                <QueueTab
                  accessToken={accessToken}
                  currentUser={currentUser}
                  queue={queue}
                  actionItemsData={{
                    items: actionItemsData.items,
                    totalCount: actionItemsData.totalCount,
                    loading: actionItemsData.isLoading,
                    refresh: actionItemsData.refresh,
                    debugInfo: actionItemsData.debugInfo
                  }}
                />
              </TabsContent>

              {/* Deliver Tab: every client's build backlog (step 4 of the simplification plan) */}
              <TabsContent value="deliver" className="space-y-6">
                <DeliverWorkspace accessToken={accessToken} onOpenClient={onViewClientDetails} />
              </TabsContent>

              {/* Analytics Tab */}
              <TabsContent value="analytics" className="space-y-6">
                <ConsultantAnalyticsDashboard accessToken={accessToken} sections="activity" />
                {/* Every AI call made for the firm's companies (GET /ai/audit/firm is firm-admin only) */}
                {isCFAdmin && <AICallLog scope="firm" />}
              </TabsContent>

              {/* Wizards Tab */}
              <TabsContent value="wizards" className="space-y-6">
                <WizardManagementPage
                  consultingFirmId={currentUser.firmId || currentUser.consultingFirmId || ''}
                  accessToken={accessToken}
                  userId={currentUser.id}
                  userName={currentUser.name || currentUser.email?.split('@')[0] || 'Consultant'}
                />
              </TabsContent>

              {/* Demo Tab */}
              {isDemo && isCFAdmin && (
                <TabsContent value="demo">
                  <div className="space-y-6">
                    <Card className="border-orange-200 bg-orange-50">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <FlaskConical className="size-5 text-orange-600" />
                          Demo Environment Tools
                        </CardTitle>
                        <CardDescription>Available in demo environments only.</CardDescription>
                      </CardHeader>
                    </Card>
                    <Card>
                      <CardHeader>
                        <CardTitle>Generate Sample Data</CardTitle>
                        <CardDescription>Populate demo companies, config items, SOWs, and queue items</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <SeedDemoDataButton 
                          accessToken={accessToken}
                          currentUser={currentUser}
                          onComplete={() => { toast.success('Refreshing dashboard...'); loadEngagements(); }}
                        />
                      </CardContent>
                    </Card>
                    <RichDemoDataCard
                      accessToken={accessToken}
                      onComplete={() => { toast.success('Refreshing dashboard...'); loadEngagements(); }}
                    />
                    <EngagementDebugger accessToken={accessToken} currentUser={currentUser} />
                    <Card>
                      <CardHeader>
                        <CardTitle>Cleanup SOWs</CardTitle>
                        <CardDescription>Remove all demo SOWs from the system</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <Button variant="destructive" onClick={() => toast.info('This feature will be implemented soon')}>
                          <Trash2 className="size-4 mr-2" />Cleanup Demo SOWs
                        </Button>
                        <p className="text-sm text-muted-foreground mt-3">
                          This will permanently delete all demo-generated SOWs. This action cannot be undone.
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>
              )}
            </Tabs>
          </>
        )}

        {/* ====== FIRM: the tenant itself ====== */}
        {activeWorkspace === 'firm' && (
          <FirmWorkspace
            accessToken={accessToken}
            currentUser={currentUser}
            engagements={engagements}
            onViewClientDetails={onViewClientDetails}
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />
        )}
      </main>

      {/* SOW Dialogs (shared across workspaces) */}
      <AddSOWDialog
        open={addSOWDialogOpen}
        onClose={() => {
          setAddSOWDialogOpen(false);
          if (createdSOW) {
            // One dashboard row per engagement (PRD §4.7): the company's other engagements are the candidate sources.
            const rows = engagements
              .filter((e) => e.companyId === createdSOW.companyId && e.engagementId)
              .map((e) => ({ id: e.engagementId as string, name: e.engagementName ?? undefined }));
            const offer = reconcileOfferFor(createdSOW, rows);
            setReconcileOffer(offer ? { ...offer, companyId: createdSOW.companyId } : null);
          }
          setCreatedSOW(null);
        }}
        accessToken={accessToken}
        consultingFirmId={currentUser.firmId || currentUser.consultingFirmId || ''}
        onSOWCreated={(created) => {
          if (created) setCreatedSOW(created);
          loadEngagements();
        }}
      />
      {reconcileOffer && (
        <ScopeReconcileDialog
          open
          onClose={() => setReconcileOffer(null)}
          companyId={reconcileOffer.companyId}
          accessToken={accessToken}
          target={reconcileOffer.target}
          engagements={reconcileOffer.engagements}
          defaultFromEngagementId={reconcileOffer.fromEngagementId}
          cancelLabel="Skip for now"
          intro={RECONCILE_AFTER_SOW_INTRO}
          onDone={() => loadEngagements()}
        />
      )}
      <SOWDetailsModal
        isOpen={sowDetailsModalOpen}
        onClose={() => setSOWDetailsModalOpen(false)}
        accessToken={accessToken}
        sowId={selectedSOWId || ''}
        projectId={projectId}
        onViewDetails={onViewSOWDetails}
        onShare={() => setShareSOWDialogOpen(true)}
        onViewVersionHistory={() => setSOWVersionHistoryOpen(true)}
        currentUser={currentUser}
      />
      {/* EditSOWDialog removed -- see ClientDetailView for rationale. */}
      <ShareSOWDialog
        open={shareSOWDialogOpen}
        onOpenChange={setShareSOWDialogOpen}
        accessToken={accessToken}
        sowId={selectedSOWId || ''}
        sowTitle="Statement of Work"
        firmId={currentUser.firmId || currentUser.consultingFirmId || ''}
      />
      <SOWVersionHistory
        open={sowVersionHistoryOpen}
        onOpenChange={setSOWVersionHistoryOpen}
        sowId={selectedSOWId || ''}
        firmId={currentUser.firmId || currentUser.consultingFirmId || ''}
        accessToken={accessToken}
      />

      {/* AI Quick SOW Dialog */}
      {aiQuickSOWOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto p-4">
          <div className="relative bg-white rounded-lg shadow-2xl max-w-5xl w-full my-8 p-6">
            <AIQuickSOW
              accessToken={accessToken}
              consultingFirm={{
                id: currentUser.firmId || currentUser.consultingFirmId,
                name: currentUser.firmName || 'Your Firm',
                defaultHourlyRate: 150,
              }}
              onClose={() => { setAIQuickSOWOpen(false); loadEngagements(); }}
            />
          </div>
        </div>
      )}
      
      {/* Add Company Dialog */}
      <Dialog open={addCompanyDialogOpen} onOpenChange={setAddCompanyDialogOpen}>
        <DialogContent className="max-w-[calc(100%-2rem)] sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Company</DialogTitle>
            <DialogDescription>Set up a new company with its first administrator</DialogDescription>
          </DialogHeader>
          <CompanySetup
            accessToken={accessToken}
            currentUser={currentUser}
            onCompanyCreated={() => {
              setAddCompanyDialogOpen(false);
              toast.success('Client company created');
              loadEngagements();
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}